import type { AuthenticationResult } from "@azure/msal-browser";

/**
 * Bridge to the native Android wrapper (receiving-wrapper-app).
 *
 * When the UI runs inside the wrapper's WebView, the native shell owns MSAL
 * authentication and exposes it via `window.NativeAuthProvider`. This module
 * lets the UI delegate SSO to that bridge instead of running its own MSAL.js
 * interactive flow (which would start a second, conflicting auth session).
 *
 * Contract mirrors receiving-wrapper-app:
 *  - specs/001-sso-msal-authentication.md
 *  - app/src/main/java/com/jbhifi/receiving/jsinterop/NativeAuthProvider.kt
 *  - app/src/main/java/com/jbhifi/receiving/jsinterop/JsAsyncCallbackHandler.kt
 *  - app/src/main/java/com/jbhifi/receiving/JsInterface.kt
 */

/** Async results are delivered as an MSAL.js-compatible AuthenticationResult, or null (no session). */
type NativeAuthResult = AuthenticationResult | null;

interface NativeAuthProviderBridge {
	/** Interactive sign-in. Fire-and-forget; the wrapper reloads the page when done. */
	login(loginHint: string | null): void;
	/** Sign out. Fire-and-forget; the wrapper reloads the page when done. */
	logout(): void;
	/** Acquire a token (silent, with interactive fallback). Result via JsAsyncCallbackHandler. */
	getToken(callbackId: string, requestJson: string): void;
	/** Restore any cached session on page load. Returns token or null via JsAsyncCallbackHandler. */
	handleRedirectPromise(callbackId: string): void;
}

interface JsAsyncCallbackHandlerBridge {
	handleAsyncResponse(callbackId: string, response: NativeAuthResult): void;
	handleAsyncError(callbackId: string, message: string): void;
}

declare global {
	interface Window {
		/** Set to true by the wrapper (JsInterface.sendNativeAppInfoToUi) before page scripts run. */
		isNativeApp?: boolean;
		/** Set to true by the wrapper (MainActivity.initWebView) when native MSAL auth is enabled. */
		isNativeAppSsoEnabled?: boolean;
		deviceIpAddress?: string;
		nativeAppVersionName?: string;
		NativeAuthProvider?: NativeAuthProviderBridge;
		JsAsyncCallbackHandler?: JsAsyncCallbackHandlerBridge;
	}
}

/**
 * True when running inside the native wrapper with the auth bridge available.
 * `window.isNativeApp` is injected in the wrapper's onPageStarted, before the
 * page's own scripts execute, so this is reliable at bootstrap.
 */
export const isNativeApp = (): boolean =>
	typeof window !== "undefined" && window.isNativeApp === true && window.NativeAuthProvider !== undefined;

/**
 * True when native SSO is enabled in the wrapper (i.e., `useMsalAuth: true` in config.json).
 * Set by the wrapper in MainActivity when initializing the WebView.
 *
 * Implies `isNativeApp()` is also true. Use this to gate native auth flows:
 * - If true: delegate auth to NativeAuthProvider
 * - If false but isNativeApp() true: fall back to MSAL.js
 * - If false: use MSAL.js normally
 */
export const isNativeAppSsoEnabled = (): boolean =>
	typeof window !== "undefined" && window.isNativeAppSsoEnabled === true;

type PendingCallback = {
	resolve: (result: NativeAuthResult) => void;
	reject: (error: Error) => void;
};

const pendingCallbacks = new Map<string, PendingCallback>();
let handlerRegistered = false;

const generateCallbackId = (): string => {
	if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
		return crypto.randomUUID();
	}
	return `cb-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

/**
 * Register `window.JsAsyncCallbackHandler` once. The wrapper calls these
 * functions (via evaluateJavascript) to deliver async native results back to
 * the page, correlated by callbackId.
 */
const ensureCallbackHandlerRegistered = (): void => {
	if (handlerRegistered) {
		return;
	}
	window.JsAsyncCallbackHandler = {
		handleAsyncResponse: (callbackId: string, response: NativeAuthResult): void => {
			const pending = pendingCallbacks.get(callbackId);
			if (!pending) {
				return;
			}
			pendingCallbacks.delete(callbackId);
			pending.resolve(response);
		},
		handleAsyncError: (callbackId: string, message: string): void => {
			const pending = pendingCallbacks.get(callbackId);
			if (!pending) {
				return;
			}
			pendingCallbacks.delete(callbackId);
			pending.reject(new Error(message || "Native authentication failed"));
		},
	};
	handlerRegistered = true;
};

const callNativeAsync = (invoke: (callbackId: string) => void): Promise<NativeAuthResult> => {
	ensureCallbackHandlerRegistered();
	return new Promise<NativeAuthResult>((resolve, reject) => {
		const callbackId = generateCallbackId();
		pendingCallbacks.set(callbackId, { resolve, reject });
		try {
			invoke(callbackId);
		} catch (error) {
			pendingCallbacks.delete(callbackId);
			reject(error instanceof Error ? error : new Error(String(error)));
		}
	});
};

/**
 * Acquire an access token for the given scopes from the native wrapper.
 * The wrapper resolves silently where possible and falls back to interactive
 * sign-in automatically.
 */
export const nativeGetToken = (scopes: string[]): Promise<AuthenticationResult> =>
	callNativeAsync((callbackId) =>
		window.NativeAuthProvider!.getToken(callbackId, JSON.stringify({ scopes }))
	).then((result) => {
		if (!result) {
			throw new Error("Native wrapper returned no authentication result");
		}
		return result;
	});

/**
 * Restore any cached broker session on page load. Resolves to the cached
 * AuthenticationResult, or null when no session exists.
 */
export const nativeHandleRedirectPromise = (): Promise<AuthenticationResult | null> =>
	callNativeAsync((callbackId) => window.NativeAuthProvider!.handleRedirectPromise(callbackId));

/** Trigger interactive sign-in via the wrapper. The wrapper reloads the page afterwards. */
export const nativeLogin = (loginHint: string | null = null): void => {
	window.NativeAuthProvider?.login(loginHint);
};

/** Sign out via the wrapper. The wrapper reloads the page afterwards. */
export const nativeLogout = (): void => {
	window.NativeAuthProvider?.logout();
};
